import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { RootState } from '../../store';
import { setCurrentDoc, updateDoc, addDoc } from '../../store/knowledgeBaseSlice';
import { knowledgeBaseAPI } from '../../api/services';
import { KBBlock, KBBlockType, KnowledgeBaseDoc } from '../../types';
import BlockRenderer from './blocks/BlockRenderer';
import BlockMenu from './blocks/BlockMenu';

const EMPTY_DOC: Partial<KnowledgeBaseDoc> = {
  title: '',
  description: '',
  icon: '📄',
  visibility: 'private',
  tags: [],
  blocks: [],
};

function makeBlock(type: KBBlockType): KBBlock {
  const base: KBBlock = { id: uuidv4(), type, content: '' };
  if (type === 'bullet_list' || type === 'ordered_list') return { ...base, data: { items: [''] } };
  if (type === 'checklist') return { ...base, data: { items: [{ text: '', checked: false }] } };
  if (type === 'table') return { ...base, data: { headers: ['Column 1', 'Column 2'], rows: [['', '']] } };
  if (type === 'toggle') return { ...base, data: { body: '', isOpen: false } };
  if (type === 'callout') return { ...base, data: { variant: 'info' } };
  if (type === 'code') return { ...base, data: { language: 'typescript' } };
  if (type === 'image') return { ...base, data: { url: '', caption: '', alt: '' } };
  if (type === 'video') return { ...base, data: { url: '', caption: '' } };
  if (type === 'file') return { ...base, data: { fileUrl: '', fileName: '', fileSize: 0 } };
  if (type === 'spacer') return { ...base, data: { size: 2 } };
  if (type === 'progress') return { ...base, data: { value: 50, label: '', color: 'blue' } };
  if (type === 'status_badge') return { ...base, data: { status: 'not_started' } };
  if (type === 'bookmark') return { ...base, data: { url: '', title: '', description: '' } };
  if (type === 'heading_1') return { ...base, data: { level: 1 } };
  if (type === 'heading_2') return { ...base, data: { level: 2 } };
  if (type === 'heading_3') return { ...base, data: { level: 3 } };
  return base;
}

const VISIBILITY_OPTIONS = [
  { value: 'private', label: 'Private', icon: '🔒' },
  { value: 'department', label: 'Department', icon: '🏢' },
  { value: 'team', label: 'Team', icon: '👥' },
  { value: 'public', label: 'Public', icon: '🌐' },
];

const KnowledgeBaseEditor: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((s: RootState) => s.auth);

  const isNew = !id || id === 'new';
  const location = useLocation();
  const linkedProjectId: string | undefined = (location.state as any)?.linkedProjectId;

  const [doc, setDoc] = useState<Partial<KnowledgeBaseDoc>>({
    ...EMPTY_DOC,
    linkedProjects: linkedProjectId ? [linkedProjectId] : [],
  });
  const [blocks, setBlocks] = useState<KBBlock[]>([]);
  const [editingBlockId, setEditingBlockId] = useState<string | null>(null);
  const [showBlockMenu, setShowBlockMenu] = useState(false);
  const [blockMenuAnchor, setBlockMenuAnchor] = useState<{ top: number; left: number } | null>(null);
  const [insertAfterIndex, setInsertAfterIndex] = useState<number>(-1);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [saveStatus, setSaveStatus] = useState<'saved' | 'unsaved' | 'saving'>('saved');
  const [showSettings, setShowSettings] = useState(false);
  const [tagInput, setTagInput] = useState('');

  // Drag-and-drop state
  const [dragFromIndex, setDragFromIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [hoveredBlockIndex, setHoveredBlockIndex] = useState<number | null>(null);

  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const settingsRef = useRef<HTMLDivElement>(null);
  const settingsBtnRef = useRef<HTMLButtonElement>(null);
  const canCreatePublic = user?.organizationRole?.canCreatePublicKnowledgeBase ?? false;
  const isAdmin = user?.role === 'admin' || (user?.organizationRole?.priority ?? 0) >= 50;

  useEffect(() => {
    if (isNew) { setLoading(false); return; }
    let mounted = true;
    knowledgeBaseAPI.get(id!)
      .then((data) => {
        if (!mounted) return;
        setDoc(data);
        setBlocks(data.blocks ?? []);
        dispatch(setCurrentDoc(data));
      })
      .catch(() => navigate('/knowledge-base'))
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [id, isNew, navigate, dispatch]);

  // Close settings when clicking outside it
  useEffect(() => {
    if (!showSettings) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        settingsRef.current && !settingsRef.current.contains(target) &&
        settingsBtnRef.current && !settingsBtnRef.current.contains(target)
      ) {
        setShowSettings(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showSettings]);

  const triggerAutoSave = useCallback(() => {
    setSaveStatus('unsaved');
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      setSaveStatus('saving');
      const payload = { ...doc, blocks };
      const req = isNew
        ? knowledgeBaseAPI.create(payload)
        : knowledgeBaseAPI.update(id!, payload);
      req
        .then((saved) => {
          setSaveStatus('saved');
          if (isNew) {
            dispatch(addDoc(saved));
            navigate(`/knowledge-base/${saved._id}/edit`, { replace: true });
          } else {
            dispatch(updateDoc(saved));
          }
        })
        .catch(() => setSaveStatus('unsaved'));
    }, 1500);
  }, [doc, blocks, isNew, id, navigate, dispatch]);

  const updateDocField = (updates: Partial<KnowledgeBaseDoc>) => {
    setDoc((prev) => ({ ...prev, ...updates }));
    triggerAutoSave();
  };

  const updateBlock = useCallback((index: number, updates: Partial<KBBlock>) => {
    setBlocks((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...updates };
      return next;
    });
    triggerAutoSave();
  }, [triggerAutoSave]);

  const addBlock = useCallback((type: KBBlockType, afterIndex: number) => {
    const block = makeBlock(type);
    setBlocks((prev) => {
      const next = [...prev];
      next.splice(afterIndex + 1, 0, block);
      return next;
    });
    setEditingBlockId(block.id);
    setSaveStatus('unsaved');
  }, []);

  const removeBlock = useCallback((index: number) => {
    setBlocks((prev) => prev.filter((_, i) => i !== index));
    setEditingBlockId(null);
    setSaveStatus('unsaved');
  }, []);

  const moveBlock = useCallback((from: number, to: number) => {
    if (from === to) return;
    setBlocks((prev) => {
      const next = [...prev];
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
    setSaveStatus('unsaved');
  }, []);

  const openBlockMenu = (e: React.MouseEvent, afterIndex: number) => {
    e.stopPropagation();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const menuHeight = 380;
    const menuWidth = 288;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceRight = window.innerWidth - rect.left;

    const top = spaceBelow >= menuHeight
      ? rect.bottom + 4
      : Math.max(8, rect.top - menuHeight - 4);

    const left = spaceRight >= menuWidth
      ? rect.left
      : Math.max(8, rect.right - menuWidth);

    setBlockMenuAnchor({ top, left });
    setInsertAfterIndex(afterIndex);
    setShowBlockMenu(true);
  };

  const handleSave = async () => {
    setSaving(true);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    try {
      const payload = { ...doc, blocks };
      const saved = isNew
        ? await knowledgeBaseAPI.create(payload)
        : await knowledgeBaseAPI.update(id!, payload);
      setSaveStatus('saved');
      if (isNew) {
        dispatch(addDoc(saved));
        navigate(`/knowledge-base/${saved._id}`, { replace: true });
      } else {
        dispatch(updateDoc(saved));
        navigate(`/knowledge-base/${saved._id}`);
      }
    } catch { /* ignore */ }
    finally { setSaving(false); }
  };

  const addTag = () => {
    const t = tagInput.trim();
    if (!t || (doc.tags ?? []).includes(t)) { setTagInput(''); return; }
    updateDocField({ tags: [...(doc.tags ?? []), t] });
    setTagInput('');
  };

  const removeTag = (tag: string) => updateDocField({ tags: (doc.tags ?? []).filter((t) => t !== tag) });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const visibleVisibility = VISIBILITY_OPTIONS.filter(
    (v) => v.value !== 'public' || canCreatePublic || isAdmin
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Top bar */}
      <div className="sticky top-0 z-40 bg-white border-b border-gray-200 px-6 py-3 flex items-center gap-3">
        <button
          onClick={() => navigate('/knowledge-base')}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Knowledge Base
        </button>
        <span className="text-gray-200">/</span>
        <span className="text-sm text-gray-700 font-medium truncate max-w-xs">{doc.title || 'Untitled'}</span>

        <div className="ml-auto flex items-center gap-3">
          <span className={`text-xs ${saveStatus === 'saved' ? 'text-green-500' : saveStatus === 'saving' ? 'text-yellow-500' : 'text-gray-400'}`}>
            {saveStatus === 'saved' ? '✓ Saved' : saveStatus === 'saving' ? 'Saving…' : 'Unsaved changes'}
          </span>

          <button
            ref={settingsBtnRef}
            onClick={() => setShowSettings((v) => !v)}
            className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Settings
          </button>

          <button
            onClick={handleSave}
            disabled={saving}
            className="text-sm bg-blue-600 text-white px-4 py-1.5 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save & View'}
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8 flex gap-8">
        {/* Main editor */}
        <div className="flex-1 min-w-0">
          {/* Document header */}
          <div className="space-y-2 mb-8">
            <div className="flex items-center gap-2">
              <button
                className="text-4xl hover:bg-gray-100 rounded-lg p-1 transition-colors"
                title="Click to change icon"
                onClick={() => {
                  const icons = ['📄', '📝', '📚', '📖', '🗒️', '📋', '📌', '🔍', '💡', '🚀', '⚡', '🎯', '🛠️', '📊'];
                  const cur = icons.indexOf(doc.icon ?? '📄');
                  updateDocField({ icon: icons[(cur + 1) % icons.length] });
                }}
              >
                {doc.icon || '📄'}
              </button>
            </div>

            <input
              type="text"
              value={doc.title ?? ''}
              onChange={(e) => updateDocField({ title: e.target.value })}
              placeholder="Untitled document"
              className="w-full text-4xl font-bold text-gray-900 border-none outline-none placeholder-gray-200 bg-transparent"
            />

            <input
              type="text"
              value={doc.description ?? ''}
              onChange={(e) => updateDocField({ description: e.target.value })}
              placeholder="Add a description…"
              className="w-full text-base text-gray-500 border-none outline-none placeholder-gray-300 bg-transparent"
            />
          </div>

          {/* Blocks */}
          <div className="space-y-1">
            {blocks.map((block, index) => {
              const isHovered = hoveredBlockIndex === index;
              const isDragTarget = dragOverIndex === index && dragFromIndex !== null && dragFromIndex !== index;
              return (
                <div
                  key={block.id}
                  className={`flex items-start gap-1 rounded-lg px-1 py-0.5 transition-colors ${
                    isDragTarget ? 'bg-blue-50 ring-2 ring-blue-300' : isHovered ? 'bg-gray-50' : ''
                  }`}
                  onMouseEnter={() => setHoveredBlockIndex(index)}
                  onMouseLeave={() => setHoveredBlockIndex(null)}
                  onDragOver={(e) => { e.preventDefault(); setDragOverIndex(index); }}
                  onDragLeave={(e) => {
                    if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOverIndex(null);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (dragFromIndex !== null && dragFromIndex !== index) moveBlock(dragFromIndex, index);
                    setDragFromIndex(null);
                    setDragOverIndex(null);
                  }}
                >
                  {/* ── Left toolbar (drag + up/down + insert) ── */}
                  <div
                    className="flex-shrink-0 flex flex-col items-center gap-0.5 pt-1 w-6"
                    style={{ opacity: isHovered ? 1 : 0, pointerEvents: isHovered ? 'auto' : 'none' }}
                  >
                    {/* Drag handle */}
                    <button
                      draggable
                      onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; setDragFromIndex(index); }}
                      onDragEnd={() => { setDragFromIndex(null); setDragOverIndex(null); }}
                      className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-200 rounded cursor-grab active:cursor-grabbing transition-colors"
                      title="Drag to reorder"
                    >
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 6a2 2 0 100-4 2 2 0 000 4zM8 14a2 2 0 100-4 2 2 0 000 4zM8 22a2 2 0 100-4 2 2 0 000 4zM16 6a2 2 0 100-4 2 2 0 000 4zM16 14a2 2 0 100-4 2 2 0 000 4zM16 22a2 2 0 100-4 2 2 0 000 4z" />
                      </svg>
                    </button>

                    {/* Move up */}
                    <button
                      onClick={(e) => { e.stopPropagation(); moveBlock(index, index - 1); }}
                      disabled={index === 0}
                      className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-200 disabled:opacity-20 disabled:pointer-events-none rounded transition-colors"
                      title="Move up"
                    >
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 15l7-7 7 7" />
                      </svg>
                    </button>

                    {/* Move down */}
                    <button
                      onClick={(e) => { e.stopPropagation(); moveBlock(index, index + 1); }}
                      disabled={index === blocks.length - 1}
                      className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-200 disabled:opacity-20 disabled:pointer-events-none rounded transition-colors"
                      title="Move down"
                    >
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {/* Insert below */}
                    <button
                      onClick={(e) => openBlockMenu(e, index)}
                      className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-blue-600 hover:bg-blue-100 rounded transition-colors font-bold text-xs leading-none"
                      title="Insert block below"
                    >
                      +
                    </button>
                  </div>

                  {/* ── Block content ── */}
                  <div
                    className="flex-1 min-w-0"
                    onClick={(e) => {
                      if ((e.target as HTMLElement).closest('button, input, textarea, select')) return;
                      setEditingBlockId(block.id);
                    }}
                  >
                    <BlockRenderer
                      block={block}
                      isEditing={editingBlockId === block.id}
                      onChange={(updates) => updateBlock(index, updates)}
                      onFinish={() => setEditingBlockId(null)}
                      allBlocks={blocks}
                    />
                  </div>

                  {/* ── Delete (right side only) ── */}
                  <button
                    onClick={(e) => { e.stopPropagation(); removeBlock(index); }}
                    className="flex-shrink-0 mt-1 w-6 h-6 flex items-center justify-center text-gray-300 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                    style={{ opacity: isHovered ? 1 : 0, pointerEvents: isHovered ? 'auto' : 'none' }}
                    title="Delete block"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              );
            })}

            {/* Append button */}
            <div className="pt-4 pl-9">
              <button
                onClick={(e) => openBlockMenu(e, blocks.length - 1)}
                className="flex items-center gap-2 text-gray-300 hover:text-gray-500 transition-colors text-sm"
              >
                <span className="w-6 h-6 flex items-center justify-center border border-dashed border-gray-200 rounded text-base hover:border-gray-400">+</span>
                <span className="text-sm">Add block</span>
              </button>
            </div>
          </div>
        </div>

        {/* Settings sidebar */}
        {showSettings && (
          <aside ref={settingsRef} className="w-72 flex-shrink-0">
            <div className="sticky top-20 bg-white border border-gray-200 rounded-xl p-5 space-y-5">
              <h3 className="text-sm font-semibold text-gray-800">Document settings</h3>

              <div className="space-y-2">
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Visibility</label>
                <div className="space-y-1">
                  {visibleVisibility.map((v) => (
                    <label key={v.value} className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="visibility"
                        value={v.value}
                        checked={doc.visibility === v.value}
                        onChange={() => updateDocField({ visibility: v.value as any })}
                        className="text-blue-600"
                      />
                      <span className="text-sm text-gray-700">{v.icon} {v.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Tags</label>
                <div className="flex flex-wrap gap-1 mb-2">
                  {(doc.tags ?? []).map((tag) => (
                    <span key={tag} className="flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">
                      {tag}
                      <button onClick={() => removeTag(tag)} className="hover:text-red-500 leading-none">&times;</button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-1">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                    placeholder="Add tag…"
                    className="flex-1 text-xs border border-gray-200 rounded px-2 py-1 outline-none focus:border-blue-400"
                  />
                  <button onClick={addTag} className="text-xs bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded transition-colors">Add</button>
                </div>
              </div>

              {!isNew && (
                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Collaborators</label>
                  <p className="text-xs text-gray-400">Manage collaborators in the document view.</p>
                </div>
              )}
            </div>
          </aside>
        )}
      </div>

      {/* Block menu portal */}
      {showBlockMenu && blockMenuAnchor && (
        <div
          className="fixed z-50"
          style={{ top: blockMenuAnchor.top, left: blockMenuAnchor.left }}
        >
          <BlockMenu
            onSelect={(type) => { addBlock(type, insertAfterIndex); }}
            onClose={() => setShowBlockMenu(false)}
          />
        </div>
      )}
    </div>
  );
};

export default KnowledgeBaseEditor;
